import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
    Plus,
    MagnifyingGlass,
    FileText,
    ArrowRight,
    Pencil,
    Trash,
    Calculator,
    TrendUp,
    Clock,
    CheckCircle,
    XCircle,
    PaperPlaneTilt,
    ArrowsClockwise,
    User,
    CalendarBlank
} from '@phosphor-icons/react';

// Stati del preventivo
const QUOTE_STATUSES = {
    bozza: { label: 'Bozza', color: 'bg-zinc-100 text-zinc-700', icon: Pencil },
    in_preparazione: { label: 'In Preparazione', color: 'bg-blue-100 text-blue-700', icon: Clock },
    inviato: { label: 'Inviato', color: 'bg-purple-100 text-purple-700', icon: PaperPlaneTilt },
    in_revisione: { label: 'In Revisione', color: 'bg-orange-100 text-orange-700', icon: ArrowsClockwise },
    accettato: { label: 'Accettato', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    rifiutato: { label: 'Rifiutato', color: 'bg-red-100 text-red-700', icon: XCircle },
    scaduto: { label: 'Scaduto', color: 'bg-zinc-100 text-zinc-500', icon: Clock },
    convertito: { label: 'Convertito', color: 'bg-emerald-100 text-emerald-700', icon: ArrowRight },
};

const PLANT_TYPES = {
    residenziale: 'Residenziale',
    aziendale: 'Aziendale',
    agrisolare: 'Agrisolare',
    altro: 'Altro'
};

export default function QuotesListPage() {
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [creating, setCreating] = useState(false);

    const loadQuotes = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (search) params.search = search;
            if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
            
            const [quotesRes, statsRes] = await Promise.all([
                quotesApi.getAll(params),
                quotesApi.getStats()
            ]);
            
            setQuotes(quotesRes.data.items || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error loading quotes:', error);
            toast.error('Errore nel caricamento preventivi');
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter]);

    useEffect(() => {
        loadQuotes();
    }, [loadQuotes]);

    const handleCreateQuote = async () => {
        try {
            setCreating(true);
            
            // Crea preventivo vuoto e naviga direttamente alla pagina di compilazione
            const payload = {
                plant_data: {
                    tipologia: 'residenziale',
                    prezzo_energia_kwh: 0.25,
                    aumento_energia_annuo: 4.0
                }
            };
            
            const response = await quotesApi.create(payload);
            toast.success(`Preventivo ${response.data.quote_number} creato - Compila i dati`);
            navigate(`/preventivi/${response.data.id}`);
        } catch (error) {
            console.error('Error creating quote:', error);
            toast.error('Errore nella creazione del preventivo');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteQuote = async (quoteId, quoteNumber) => {
        if (!window.confirm(`Eliminare il preventivo ${quoteNumber}?`)) return;
        
        try {
            await quotesApi.delete(quoteId);
            toast.success('Preventivo eliminato');
            loadQuotes();
        } catch (error) {
            console.error('Error deleting quote:', error);
            toast.error(error.response?.data?.detail || 'Errore eliminazione');
        }
    };

    const getCustomerName = (customer) => {
        if (!customer) return '';
        if (customer.tipo === 'azienda' && customer.ragione_sociale) {
            return customer.ragione_sociale;
        }
        return `${customer.nome || ''} ${customer.cognome || ''}`.trim();
    };

    const formatCurrency = (value) => {
        if (!value && value !== 0) return '-';
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('it-IT');
    };

    return (
        <div className="p-6 space-y-6" data-testid="quotes-list-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-900">Preventivi</h1>
                    <p className="text-zinc-500">Gestione preventivi fotovoltaici</p>
                </div>
                <Button 
                    onClick={handleCreateQuote}
                    disabled={creating}
                    className="gap-2"
                    data-testid="new-quote-btn"
                >
                    {creating ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                        <Plus className="w-4 h-4" />
                    )}
                    Nuovo Preventivo
                </Button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                    <Calculator className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-500">Totale Preventivi</p>
                                    <p className="text-xl font-bold">{stats.total_quotes}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                                    <CalendarBlank className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-500">Questo Mese</p>
                                    <p className="text-xl font-bold">{stats.quotes_this_month}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                    <TrendUp className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-500">Tasso Conversione</p>
                                    <p className="text-xl font-bold">{stats.conversion_rate}%</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-500">In Bozza</p>
                                    <p className="text-xl font-bold">{stats.by_status?.bozza?.count || 0}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                            <Input
                                placeholder="Cerca per numero o cliente..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9"
                                data-testid="quote-search"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                                <SelectValue placeholder="Tutti gli stati" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti gli stati</SelectItem>
                                {Object.entries(QUOTE_STATUSES).map(([key, status]) => (
                                    <SelectItem key={key} value={key}>{status.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Quotes Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : quotes.length === 0 ? (
                        <div className="text-center py-12">
                            <Calculator className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
                            <p className="text-zinc-500">Nessun preventivo trovato</p>
                            <Button 
                                variant="outline" 
                                className="mt-4"
                                onClick={handleCreateQuote}
                                disabled={creating}
                            >
                                Crea il primo preventivo
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-zinc-50 border-b">
                                    <tr>
                                        <th className="text-left p-4 font-medium text-zinc-600">Numero</th>
                                        <th className="text-left p-4 font-medium text-zinc-600">Cliente</th>
                                        <th className="text-left p-4 font-medium text-zinc-600">Impianto</th>
                                        <th className="text-left p-4 font-medium text-zinc-600">Stato</th>
                                        <th className="text-right p-4 font-medium text-zinc-600">Totale</th>
                                        <th className="text-left p-4 font-medium text-zinc-600">Data</th>
                                        <th className="text-right p-4 font-medium text-zinc-600">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {quotes.map((quote) => {
                                        const status = QUOTE_STATUSES[quote.status] || QUOTE_STATUSES.bozza;
                                        const StatusIcon = status.icon;
                                        return (
                                            <tr 
                                                key={quote.id} 
                                                className="hover:bg-zinc-50 cursor-pointer"
                                                onClick={() => navigate(`/preventivi/${quote.id}`)}
                                                data-testid={`quote-row-${quote.id}`}
                                            >
                                                <td className="p-4">
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="w-4 h-4 text-zinc-400" />
                                                        <span className="font-mono font-medium text-zinc-900">
                                                            {quote.quote_number}
                                                        </span>
                                                        {quote.revision > 1 && (
                                                            <Badge variant="outline" className="text-xs">
                                                                v{quote.revision}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-2">
                                                        <User className="w-4 h-4 text-zinc-400" />
                                                        <span className="text-zinc-700">
                                                            {quote.customer_name || '-'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="text-sm">
                                                        <span className="font-medium">
                                                            {quote.plant_data?.potenza_kwp || '-'} kWp
                                                        </span>
                                                        {quote.plant_data?.accumulo_presente && (
                                                            <span className="text-zinc-500 ml-2">
                                                                + {quote.plant_data.capacita_accumulo_kwh || '-'} kWh
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-zinc-500">
                                                        {PLANT_TYPES[quote.plant_data?.tipologia] || '-'}
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <Badge className={`${status.color} gap-1`}>
                                                        <StatusIcon className="w-3 h-3" />
                                                        {status.label}
                                                    </Badge>
                                                </td>
                                                <td className="p-4 text-right">
                                                    <span className="font-semibold text-zinc-900">
                                                        {formatCurrency(quote.total_gross)}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-zinc-500 text-sm">
                                                    {formatDate(quote.quote_date || quote.created_at)}
                                                </td>
                                                <td className="p-4 text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/preventivi/${quote.id}`);
                                                            }}
                                                        >
                                                            <ArrowRight className="w-4 h-4" />
                                                        </Button>
                                                        {quote.status === 'bozza' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteQuote(quote.id, quote.quote_number);
                                                                }}
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <Trash className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

        </div>
    );
}

